// ================================================================
// 🤝 COMMISSIONS REPORT - Relatório de Comissões
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import StatCard from '../../components/common/StatCard';
import FilterBar from '../../components/reports/FilterBar';
import FilterDrawer from '../../components/reports/FilterDrawer';
import SmartTable from '../../components/reports/SmartTable';
import ReportsService from '../../services/api/reports';
import MasterLayout from '../../masterLayout/MasterLayout';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import '../../components/reports/Reports.css';
import { useChartsColors } from '../../hooks/useChartsColors'; 

// Registrar componentes do ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const CommissionsReport = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commissionsData, setCommissionsData] = useState(null);
  const [filters, setFilters] = useState({
    period: '30d',
    advancedFilters: {}
  });
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const chartsColors = useChartsColors();
  /**
   * Handle mudança de filtros de usuário
   */
  const handleUserFilterChange = (userFilters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        advancedFilters: {
          ...prev.advancedFilters,
          user_type: userFilters.user_type || undefined,
          user_id: userFilters.user_id || undefined
        }
      };
      // Recarrega imediatamente com os novos filtros
      setTimeout(() => {
        loadCommissionsReportWithFilters(newFilters);
      }, 50);
      return newFilters;
    });
  };

  /**
   * Carrega relatório com filtros específicos (usado quando filtros mudam)
   */
  const loadCommissionsReportWithFilters = useCallback(async (filtersToUse) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🤝 Carregando Relatório de Comissões com filtros...', filtersToUse);

      const result = await ReportsService.getCommissionsReport({
        period: filtersToUse.period,
        user_type: filtersToUse.advancedFilters?.user_type,
        user_id: filtersToUse.advancedFilters?.user_id,
        product_id: filtersToUse.advancedFilters?.product_id,
        payment_method: filtersToUse.advancedFilters?.payment_method,
        sale_status: filtersToUse.advancedFilters?.sale_status,
        region: filtersToUse.advancedFilters?.region
      });

      if (result.success) {
        setCommissionsData(result.data);
        console.log('✅ Relatório de Comissões carregado com filtros:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Carrega dados do relatório
   */
  const loadCommissionsReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🤝 Carregando Relatório de Comissões...', filters);

      const result = await ReportsService.getCommissionsReport({
        period: filters.period,
        user_type: filters.advancedFilters?.user_type,
        user_id: filters.advancedFilters?.user_id,
        product_id: filters.advancedFilters?.product_id,
        payment_method: filters.advancedFilters?.payment_method,
        sale_status: filters.advancedFilters?.sale_status,
        region: filters.advancedFilters?.region
      });

      if (result.success) {
        setCommissionsData(result.data);
        console.log('✅ Relatório de Comissões carregado:', result.data);
      } else {
        throw new Error(result.message || 'Erro ao carregar relatório');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar relatório:', err);
      setError(err.response?.data?.message || err.message || 'Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /**
   * Carrega relatório ao montar componente
   */
  useEffect(() => {
    loadCommissionsReport();
  }, [loadCommissionsReport]);

  /**
   * Handle mudança de período
   */
  const handlePeriodChange = (newPeriod) => {
    setFilters(prev => ({
      ...prev,
      period: newPeriod
    }));
  };

  /**
   * Handle filtros avançados
   */
  const handleAdvancedFilters = (advancedFilters) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        advancedFilters
      };
      // Recarrega imediatamente com os novos filtros
      setTimeout(() => {
        loadCommissionsReportWithFilters(newFilters);
      }, 50);
      return newFilters;
    });
  };

  /**
   * Handle exportar CSV
   */
  const handleExport = async () => {
    try {
      const blob = await ReportsService.exportCSV('commissions', filters);
      ReportsService.downloadCSV(blob, `relatorio-comissoes-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (err) {
      console.error('❌ Erro ao exportar CSV:', err);
      alert('Erro ao exportar CSV. Esta funcionalidade será implementada em breve.');
    }
  };

  /**
   * Mapa de nomes de roles
   */
  const roleLabels = {
    PRODUCER: 'Produtor',
    AFFILIATE: 'Afiliado',
    PLATFORM: 'Plataforma',
    COPRODUCER: 'Coprodutor',
    MANAGER: 'Gerente',
    SELLER: 'Vendedor',
    SUPPLIER: 'Fornecedor',
    LOGISTICS_REP: 'Representante Logística'
  };

  /**
   * Gráfico de comissões por role
   */
  const commissionsByRoleChart = commissionsData?.charts?.commissionsByRole ? {
    data: {
      labels: commissionsData.charts.commissionsByRole.map(item => roleLabels[item.role_code] || item.role_name),
      datasets: [
        {
          data: commissionsData.charts.commissionsByRole.map(item => item.total_amount / 100),
          backgroundColor: [
            '#007bff',
            '#28a745',
            '#ffc107',
            '#dc3545',
            '#17a2b8',
            '#6c757d',
            '#6f42c1',
            '#fd7e14'
          ],
          borderWidth: 0
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed;
              return `${context.label}: R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
            }
          }
        }
      }
    }
  } : null;

  /**
   * Gráfico de tendência de comissões
   */
  const commissionsTrendChart = commissionsData?.charts?.commissionsTrend ? {
    data: {
      labels: commissionsData.charts.commissionsTrend.map(item => 
        new Date(item.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
      ),
      datasets: [
        {
          label: 'Comissões (R$)',
          data: commissionsData.charts.commissionsTrend.map(item => item.total_amount / 100),
          borderColor: '#007bff',
          backgroundColor: 'rgba(0, 123, 255, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => `R$ ${context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        }
      },
      scales: {
        x: {
          grid: {
            color: chartsColors
          }
        },
        y: {
          grid: {
            color: chartsColors
          },
          beginAtZero: true,
          ticks: {
            callback: (value) => `R$ ${value.toLocaleString('pt-BR')}`
          }
        }
      }
    }
  } : null;

  /**
   * Colunas da tabela de comissões
   */
  const commissionsColumns = [
    { key: 'commission_id', label: 'ID', sortable: true },
    { key: 'sale_date', label: 'Data', format: 'datetime', sortable: true },
    { key: 'product_name', label: 'Produto', sortable: true },
    { 
      key: 'role_name', 
      label: 'Tipo',
      sortable: true,
      render: (value, row) => {
        const colors = {
          PRODUCER: 'primary',
          AFFILIATE: 'success',
          PLATFORM: 'warning',
          COPRODUCER: 'info',
          MANAGER: 'secondary',
          SELLER: 'dark',
          SUPPLIER: 'danger',
          LOGISTICS_REP: 'light'
        };
        return <span className={`badge bg-${colors[row.role_code] || 'secondary'}`}>{roleLabels[row.role_code] || value}</span>;
      }
    },
    { key: 'recipient_name', label: 'Destinatário', sortable: true },
    { key: 'gross_amount', label: 'Valor Bruto', format: 'currency', sortable: true },
    { key: 'commission_amount', label: 'Comissão', format: 'currency', sortable: true },
    { key: 'net_amount', label: 'Valor Líquido', format: 'currency', sortable: true },
    { 
      key: 'commission_rate', 
      label: 'Taxa (%)',
      sortable: true,
      render: (value) => `${(value || 0).toFixed(2)}%`
    },
    { 
      key: 'status', 
      label: 'Status',
      sortable: true,
      render: (value) => {
        const colors = {
          paid: 'success',
          pending: 'warning',
          cancelled: 'danger'
        };
        const labels = {
          paid: 'Pago',
          pending: 'Pendente',
          cancelled: 'Cancelado'
        };
        return <span className={`badge bg-${colors[value] || 'secondary'}`}>{labels[value] || value}</span>;
      }
    }
  ];

  return (
    <MasterLayout>
      <Container fluid style={{ padding: '32px 24px', minHeight: '100vh' }}>
      {/* Cabeçalho com Botões */}
      <div style={{
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #e9ecef',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div style={{ flex: '1', minWidth: '300px' }}>
          <div style={{
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Icon icon="mdi:account-cash" style={{ color: '#28a745', fontSize: '22px' }} />
            <span className='text-contrast' style={{
              fontSize: '22px',
              fontWeight: '700',
              // color: '#212529'
            }}>
              Relatório de Comissões
            </span>
          </div>
          <p style={{
            fontSize: '16px',
            color: '#6c757d',
            margin: 0
          }}>
            Análise detalhada de comissões por role, status e período
          </p>
        </div>
        
        {/* Botões de Ação */}
        <div style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <Button
            variant={Object.keys(filters.advancedFilters || {}).filter(k => k !== 'user_type' && k !== 'user_id' && filters.advancedFilters[k]).length > 0 ? 'primary' : 'outline-primary'}
            onClick={() => setShowFilterDrawer(true)}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon icon="mdi:filter" style={{ fontSize: '18px' }} />
            Filtros Avançados
          </Button>

          <Button
            variant="outline-success"
            onClick={handleExport}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon icon="mdi:file-excel" style={{ fontSize: '18px' }} />
            Exportar CSV
          </Button>

          <Button
            variant="primary"
            onClick={loadCommissionsReport}
            disabled={loading}
            style={{
              borderRadius: '8px',
              padding: '10px 20px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Icon 
              icon="mdi:refresh" 
              style={{ fontSize: '18px' }}
              className={loading ? 'spin' : ''}
            />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <FilterBar
        period={filters.period}
        onPeriodChange={handlePeriodChange}
        onApplyAdvancedFilters={handleAdvancedFilters}
        onRefresh={loadCommissionsReport}
        onExport={handleExport}
        loading={loading}
        advancedFilters={filters.advancedFilters}
        onUserFilterChange={handleUserFilterChange}
        showExport={false}
      />

      {/* Drawer de Filtros Avançados */}
      <FilterDrawer
        show={showFilterDrawer}
        onHide={() => setShowFilterDrawer(false)}
        onApply={(newFilters) => {
          setShowFilterDrawer(false);
          handleAdvancedFilters(newFilters);
        }}
        currentFilters={filters.advancedFilters}
      />

      {/* Erro */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Icon icon="mdi:alert-circle" style={{ marginRight: '8px' }} />
          {error}
        </Alert>
      )}

      {/* KPIs */}
      <Row className="g-4 mb-5">
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Total de Comissões"
            value={loading ? '...' : (commissionsData?.kpis?.total_commissions || 0).toLocaleString('pt-BR')}
            subtitle={loading ? '' : `${commissionsData?.kpis?.unique_recipients || 0} destinatários únicos`}
            icon="mdi:handshake"
            variant="blue"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Comissões Pagas"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(commissionsData?.kpis?.paid_amount || 0)}
            subtitle={loading ? '' : 'Total já pago'}
            icon="mdi:cash-check"
            variant="green"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Comissões Pendentes"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(commissionsData?.kpis?.pending_amount || 0)}
            subtitle={loading ? '' : 'Aguardando pagamento'}
            icon="mdi:clock-outline"
            variant="orange"
          />
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <StatCard
            title="Comissão Média"
            value={loading ? '...' : CurrencyFormatter.centsToFormatted(commissionsData?.kpis?.avg_commission || 0)}
            subtitle={loading ? '' : 'Média por transação'}
            icon="mdi:chart-line"
            variant="purple"
          />
        </Col>
      </Row>

      {/* Gráficos */}
      <Row className="g-4 mb-5">
        {/* Comissões por Role */}
        <Col xs={12} lg={6}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '400px'
          }}>
            <Card.Body>
              <span className='text-constrast' style={{
                fontSize: '18px',
                fontWeight: '600',
                // color: '#212529',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:chart-donut" style={{ color: '#007bff' }} />
                Distribuição por Tipo
              </span>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : commissionsByRoleChart ? (
                <div style={{ height: '300px' }}>
                  <Doughnut data={commissionsByRoleChart.data} options={commissionsByRoleChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Tendência de Comissões */}
        <Col xs={12} lg={6}>
          <Card style={{
            border: 'none',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            height: '400px'
          }}>
            <Card.Body>
              <span className='text-contrsat' style={{
                fontSize: '18px',
                fontWeight: '600',
                // color: '#212529',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Icon icon="mdi:chart-timeline-variant" style={{ color: '#28a745' }} />
                Tendência ao Longo do Tempo
              </span>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
                  <Icon icon="mdi:loading" className="spin" style={{ fontSize: '48px', color: '#007bff' }} />
                </div>
              ) : commissionsTrendChart ? (
                <div style={{ height: '300px' }}>
                  <Line data={commissionsTrendChart.data} options={commissionsTrendChart.options} />
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#6c757d' }}>
                  Sem dados para exibir
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Lista de Comissões */}
      <Row>
        <Col xs={12}>
          <span className='text-contrast' style={{
            fontSize: '18px',
            fontWeight: '600',
            // color: '#212529',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Icon icon="mdi:format-list-bulleted" style={{ color: '#6c757d' }} />
            Lista de Comissões (últimas 100)
          </span>
          <SmartTable
            data={commissionsData?.commissionsList || []}
            columns={commissionsColumns}
            loading={loading}
            searchable={true}
            itemsPerPage={20}
            emptyMessage="Nenhuma comissão encontrada"
          />
        </Col>
      </Row>
      </Container>
    </MasterLayout>
  );
};

export default CommissionsReport;

